import { Children, FC, ReactNode, useId } from "react";

import { BaseProps } from "../../BaseProps";
import { Heading } from "../../typography";
import { getHtmlPropsFromBaseProps } from "../../utilities/getHtmlPropsFromBaseProps";

import legacyStyles from "./legacy/styles.module.scss";
import tokenStyles from "./styles.module.css";
import { createComponentStyleHook } from "../../tokens";

/** The `Group` component properties. */
export interface GroupProps extends BaseProps {
  /** The section title. */
  readonly title?: string;

  /** The component children. */
  readonly children?: ReactNode;

  /** The heading level for the title (default: 4, due to Heading component default). */
  readonly headingLevel?: 2 | 3 | 4 | 5 | 6;
}

const useStyles = createComponentStyleHook({
  legacyStyles,
  tokenStyles,
  componentTokens: "sidebar_nav",
});

/** The in-context sidebar navigation component. */
export const Group: FC<GroupProps> = (props) => {
  const { title, children, headingLevel } = props;
  const { mode, styles, StyleModeBoundary } = useStyles(props);
  const uniqueId = useId();

  const groupBaseProps = getHtmlPropsFromBaseProps(props, {
    className: [styles.Group, "group"],
  });

  const headingId = title ? `sidebar-group-${uniqueId}` : undefined;

  return (
    <StyleModeBoundary mode={mode}>
      <div {...groupBaseProps} aria-labelledby={headingId}>
        {title && (
          <Heading level={headingLevel} className="title" id={headingId}>
            {title}
          </Heading>
        )}
        <ul>
          {Children.map(children, (child, index) => (
            <li key={index}>{child}</li>
          ))}
        </ul>
      </div>
    </StyleModeBoundary>
  );
};
